import React, { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import {
  Card,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Icon from "@mui/material/Icon";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";

function StockItems() {
  const { site_id } = useParams();
  const [siteName, setSiteName] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const formattedDate = new Date().toISOString().split("T")[0]; // e.g. 2025-06-11
  const safeSiteName = siteName.replace(/[^a-z0-9]/gi, "_").toLowerCase(); // sanitise filename
  const csvFileName = `${safeSiteName || "site"}_stock_items_${formattedDate}.csv`;
  const [uploadedItems, setUploadedItems] = useState([]);
  const roleId = parseInt(localStorage.getItem("role_id"));
  const fileInputRef = React.useRef();
  const [newItem, setNewItem] = useState({
    component: "",
    manufacturer: "",
    part_number: "",
    quantity: 0,
    alarm_notification: 0,
    compatibility: "",
    wholesaler: "",
    cost: 0,
    lead_time: "",
    site_id: site_id,
  });
  const [sites, setSites] = useState([]);
  const navigate = useNavigate();

  const fetchSiteName = async () => {
    try {
      const siteResponse = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
      );
      const siteData = await siteResponse.json();
      setSiteName(siteData.name || "site"); // default to 'site' if no name
    } catch (error) {
      console.error("Error fetching site name:", error);
    }
  };

  // Fetch stock items for the current site
  const fetchStockItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/stock-items?site_id=${site_id}`
      );
      const data = await response.json();
      // Ensure data is always an array and handle decimal costs
      const formattedData = Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            cost: parseFloat(item.cost) || 0,
            compatibility: Array.isArray(item.compatibility) ? item.compatibility : [],
          }))
        : [];
      setStockItems(formattedData);
    } catch (error) {
      console.error("Error fetching stock items:", error);
      setStockItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all sites for the dropdown
  const fetchSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("user_id");
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/sites?user_id=${userId}`,
        {
          headers: { Authorization: token },
        }
      );
      const data = await response.json();
      setSites(data || []);
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  };

  useEffect(() => {
    fetchStockItems();
    fetchSites();
    fetchSiteName();
  }, [site_id]);

  const handleAddItem = async () => {
    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/stock-items",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newItem),
        }
      );
      console.log(newItem);

      if (!response.ok) throw new Error("Failed to add stock item");

      setAddDialogOpen(false);
      setNewItem({
        component: "",
        manufacturer: "",
        part_number: "",
        quantity: 0,
        alarm_notification: 0,
        compatibility: "",
        wholesaler: "",
        cost: 0,
        lead_time: "",
        site_id: site_id,
      });
      fetchStockItems();
    } catch (error) {
      console.error("Error adding stock item:", error);
    }
  };

  // Handle update item
  const handleUpdateItem = async () => {
    if (!currentItem) return;

    try {
      // Ensure lead_time has a value before sending
      const itemToUpdate = {
        ...currentItem,
        lead_time: currentItem.lead_time || 0,
      };

      await axios.put(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/stock-items/${currentItem.id}`,
        itemToUpdate
      );

      setEditDialogOpen(false);
      fetchStockItems();
    } catch (error) {
      console.error("Error updating stock item:", error);
    }
  };

  // Handle delete item
  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Are you sure you want to delete this stock item?")) {
      try {
        await axios.delete(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/stock-items/${itemId}`
        );
        fetchStockItems();
      } catch (error) {
        console.error("Error deleting stock item:", error);
      }
    }
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      // Optional: map column names to match your backend API
      const parsedItems = jsonData.map((row) => ({
        component: row.Component || "",
        manufacturer: row.Manufacturer || "",
        part_number: String(row.PartNumber || "").trim(),
        quantity: parseInt(row.Quantity) || 0,
        alarm_notification: parseInt(row.AlarmThreshold) || 0,
        compatibility: row.Compatibility?.split(",") || [],
        wholesaler: row.Wholesaler || "",
        cost: parseFloat(String(row.Cost).replace("£", "")) || 0,
        lead_time: parseInt(row.LeadTime) || 0,
        site_id: site_id,
      }));

      // Optional: preview or directly upload to server
      uploadStockItems(parsedItems);
    };
    reader.readAsArrayBuffer(file);
  };

  const uploadStockItems = async (items) => {
    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/stock-items/bulk",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(items),
        }
      );

      if (!response.ok) throw new Error("Failed to upload stock items");

      fetchStockItems();
      alert("Stock items uploaded successfully!");
    } catch (error) {
      console.error("Error uploading stock items:", error);
      alert("Failed to upload stock items.");
    }
  };

  // Columns for the table
  const columns = [
    { name: "Component", align: "left" },
    { name: "Manufacturer", align: "left" },
    { name: "Part Number", align: "left" },
    { name: "Quantity", align: "center" },
    { name: "Alarm", align: "center" },
    {
      name: "Compatibility",
      align: "left",
      render: (compatibility) => (
        <div>
          {compatibility && compatibility.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {compatibility.map((task, index) => (
                <li key={index}>{task}</li>
              ))}
            </ul>
          ) : (
            <span>No tasks using this part</span>
          )}
        </div>
      ),
    },
    { name: "Wholesaler", align: "left" },
    {
      name: "Cost",
      align: "center",
      render: (cost) => `£${parseFloat(cost).toFixed(2)}`,
    },
    { name: "Lead Time", align: "center" },
    { name: "Actions", align: "center" },
  ];

  // Modify the rows mapping to pass compatibility data
  const rows = stockItems.map((item) => ({
    Component: item.component,
    Manufacturer: item.manufacturer,
    "Part Number": item.part_number,
    Quantity: item.quantity,
    Alarm: item.alarm_notification,
    Compatibility:
      item.compatibility?.length > 0 ? (
        <span>{item.compatibility.join(", ")}</span>
      ) : (
        <span>No tasks using this part</span>
      ),
    Wholesaler: item.wholesaler,
    Cost: item.cost,
    "Lead Time": item.lead_time
      ? `${parseInt(item.lead_time)} day${parseInt(item.lead_time) !== 1 ? "s" : ""}`
      : "N/A",
    Actions: (
      <>
        <IconButton
          color="info"
          onClick={() => {
            setCurrentItem(item);
            setEditDialogOpen(true);
          }}
        >
          <Icon>edit</Icon>
        </IconButton>
        <IconButton color="error" onClick={() => handleDeleteItem(item.id)}>
          <Icon>delete</Icon>
        </IconButton>
      </>
    ),
  }));

  const csvData = stockItems.map((item) => ({
    Component: item.component,
    Manufacturer: item.manufacturer,
    PartNumber: item.part_number,
    Quantity: item.quantity,
    AlarmThreshold: item.alarm_notification,
    Wholesaler: item.wholesaler,
    Cost: `£${parseFloat(item.cost).toFixed(2)}`,
    LeadTime: item.lead_time ? `${item.lead_time} days` : "N/A",
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              Parts Management
            </VuiTypography>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <input
                type="file"
                accept=".xlsx, .xls"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleExcelUpload}
              />

              {roleId === 3 && (
                <VuiButton
                  variant="contained"
                  color="secondary"
                  onClick={() => fileInputRef.current.click()}
                >
                  <Icon>upload</Icon>&nbsp; Upload Excel
                </VuiButton>
              )}

              <CSVLink data={csvData} filename={csvFileName}>
                <VuiButton variant="contained" color="info">
                  <Icon>download</Icon>&nbsp; Export CSV
                </VuiButton>
              </CSVLink>
              <VuiButton variant="contained" color="primary" onClick={() => setAddDialogOpen(true)}>
                <Icon>add</Icon>&nbsp; Add Stock Item
              </VuiButton>
            </div>
          </VuiBox>
          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : (
            <Table columns={columns} rows={rows} />
          )}
        </Card>
      </VuiBox>

      {/* Add Item Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Stock Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Component"
                margin="dense"
                value={newItem.component}
                onChange={(e) => setNewItem({ ...newItem, component: e.target.value })}
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                margin="dense"
                value={newItem.manufacturer}
                onChange={(e) => setNewItem({ ...newItem, manufacturer: e.target.value })}
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Part Number"
                margin="dense"
                value={newItem.part_number}
                onChange={(e) => setNewItem({ ...newItem, part_number: e.target.value })}
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Quantity"
                margin="dense"
                value={newItem.quantity}
                onChange={(e) =>
                  setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })
                }
                required
                inputProps={{ min: 0 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Alarm Notification Threshold"
                margin="dense"
                value={newItem.alarm_notification}
                onChange={(e) =>
                  setNewItem({ ...newItem, alarm_notification: parseInt(e.target.value) || 0 })
                }
                required
                inputProps={{ min: 0 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Wholesaler"
                margin="dense"
                value={newItem.wholesaler}
                onChange={(e) => setNewItem({ ...newItem, wholesaler: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Cost"
                margin="dense"
                value={newItem.cost}
                onChange={(e) => setNewItem({ ...newItem, cost: parseFloat(e.target.value) || 0 })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputProps: { min: 0, style: { fontSize: "0.875rem" } },
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Lead Time (days)"
                margin="dense"
                value={newItem.lead_time}
                onChange={(e) =>
                  setNewItem({ ...newItem, lead_time: parseInt(e.target.value) || 0 })
                }
                inputProps={{ min: 0 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "0.875rem" },
                }}
                InputProps={{
                  style: { fontSize: "0.875rem", width: '100%' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            color="primary"
            disabled={!newItem.manufacturer || !newItem.part_number}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Stock Item</DialogTitle>
        <DialogContent>
          {currentItem && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Component"
                  margin="dense"
                  value={currentItem.component}
                  onChange={(e) => setCurrentItem({ ...currentItem, component: e.target.value })}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  margin="dense"
                  value={currentItem.manufacturer}
                  onChange={(e) => setCurrentItem({ ...currentItem, manufacturer: e.target.value })}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Part Number"
                  margin="dense"
                  value={currentItem.part_number}
                  onChange={(e) => setCurrentItem({ ...currentItem, part_number: e.target.value })}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  margin="dense"
                  value={currentItem.quantity}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) })
                  }
                  inputProps={{ min: 0 }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Alarm Notification Threshold"
                  margin="dense"
                  value={currentItem.alarm_notification}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      alarm_notification: parseInt(e.target.value),
                    })
                  }
                  inputProps={{ min: 0 }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wholesaler"
                  margin="dense"
                  value={currentItem.wholesaler}
                  onChange={(e) => setCurrentItem({ ...currentItem, wholesaler: e.target.value })}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost"
                  margin="dense"
                  value={currentItem.cost}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      cost: parseFloat(e.target.value) || 0,
                    })
                  }
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    inputProps: { min: 0 },
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Lead Time (days)"
                  margin="dense"
                  value={currentItem.lead_time}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      lead_time: parseInt(e.target.value) || 0,
                    })
                  }
                  inputProps={{ min: 0 }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { width: '100%' }, 
                    '& .MuiInputBase-input': { width: '100% !important' } 
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "0.875rem" },
                  }}
                  InputProps={{
                    style: { fontSize: "0.875rem", width: '100%' },
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdateItem} color="primary">
            Update Item
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default StockItems;